import React, {useRef, useState} from 'react';
import {
	Button,
	ButtonDropdownField,
	Checkbox,
	ComboBox,
	DropdownList,
	FlexColumn,
	FlexContainer,
	FlexRow,
	Icon,
	IconTooltip,
	Label,
	Modal,
	Text,
	TextField,
} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import Util from '../../../forecast-app/shared/util/util';
import UpdateClientMutation from '../../../mutations/update_client_mutation';
import {getDefaultMediumClientLogo} from '../../../components/default_avatars';
import {BUTTON_COLOR, BUTTON_STYLE, isoCountries, MODULE_TYPES} from '../../../constants';
import {createFragmentContainer, graphql} from 'react-relay';
import Warning from '../../../components/warning';
import type {EditClientDetailsModal_viewer$data as EditClientDetailsModal_viewer} from './__generated__/EditClientDetailsModal_viewer.graphql';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {showChangesSavedToast} from '../../../forecast-app/shared/components/toasts/toast';
import {clientLogoSrc} from '../../../directApi';
import {hasModule} from '../../shared/util/ModuleUtil';
import {hasFeatureFlag} from '../../shared/util/FeatureUtil';
import {isCustomerPortalAvailable} from '../../shared/util/customerPortalAvailability';
import UpdateClientPortalConfigMutation from '../../../mutations/update_client_portal_config_mutation';
import {ClientPrimaryContactDropdown} from '../../shared/components/dropdowns/client-primary-contact-dropdown/ClientPrimaryContactDropdown';
import WorkspaceUsersSection from './WorkspaceUsersSection';

type IsoCountryType = {value: string; label: string};

type ClientStatus = {id: string; label: string; standing: string};

type Contact = {id: string; name: string};

type EditClientDetailsModalProps = {
	clientId: number;
	closeModal: () => void;
	viewer: EditClientDetailsModal_viewer;
};

enum LogoState {
	NO_CHANGE = 'NO_CHANGE',
	UPDATE = 'UPDATE',
	DELETE = 'DELETE',
}

// The portal stores the account manager as a raw Forecast person id, while the
// person list speaks Relay global ids. Decode so a persisted owner can be matched
// back to its entry in the list.
const decodePersonId = (globalId: string): number | null => {
	const raw = Number(Util.getUUIdFromBase64String(globalId));
	return Number.isFinite(raw) ? raw : null;
};

const hasAddressData = (client: NonNullable<EditClientDetailsModal_viewer['company']>['client']): boolean =>
	!!(client?.street || client?.zip || client?.city || client?.country || client?.region || client?.vat);

export const EditClientDetailsModal = ({clientId, viewer, closeModal}: EditClientDetailsModalProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const {client} = viewer?.company || {};
	const clientStatuses: ClientStatus[] =
		viewer?.company?.clientStatuses?.edges?.flatMap(e => (e?.node ? [e.node as ClientStatus] : [])) ?? [];
	const changeAvatarButtonRef = useRef<HTMLInputElement>(null);

	const [name, setName] = useState<string>(client?.name || '');
	const [website, setWebsite] = useState<string>(client?.website || '');
	const [region, setRegion] = useState<string>(client?.region || '');
	const [statusId, setStatusId] = useState<string | null>(client?.status?.id ?? null);
	const [primaryContact, setPrimaryContact] = useState<Contact | null>(
		client?.primaryContact
			? {
					id: client.primaryContact.id,
					name: [client.primaryContact.firstName, client.primaryContact.lastName].filter(Boolean).join(' '),
			  }
			: null
	);
	const [street, setStreet] = useState<string>(client?.street || '');
	const [zip, setZip] = useState<string>(client?.zip || '');
	const [city, setCity] = useState<string>(client?.city || '');
	const [vat, setVat] = useState<string>(client?.vat || '');
	const [country, setCountry] = useState<IsoCountryType | null>(
		isoCountries.find((c: IsoCountryType) => c.value === client?.country) || null
	);
	const [isAddressExpanded, setIsAddressExpanded] = useState<boolean>(hasAddressData(client ?? null));

	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [logoState, setLogoState] = useState<LogoState>(LogoState.NO_CHANGE);
	const [logo, setLogo] = useState<string>(
		client?.logoId ? clientLogoSrc(client.logoId) : getDefaultMediumClientLogo(client?.logoDefaultId)
	);

	// FOR-48 (BU-10) 10a — Client Portal section. Config is read from the portal
	// via the clientPortalConfig fragment field and saved through the portal proxy
	// mutation (never forecast_db). The section is availability-gated and, when the
	// portal is inactive, rendered read-only with an "Activate portal" prompt.
	const portalConfig = client?.portalConfig ?? null;
	const showPortalSection = isCustomerPortalAvailable();
	const [portalActive, setPortalActive] = useState<boolean>(!!portalConfig?.portalActive);
	const [portalPrimaryColour, setPortalPrimaryColour] = useState<string>(portalConfig?.portalPrimaryColour || '');
	const [portalSenderName, setPortalSenderName] = useState<string>(portalConfig?.portalSenderName || '');
	const [portalShowInvoices, setPortalShowInvoices] = useState<boolean>(!!portalConfig?.showInvoices);
	const [portalAllowFileUpload, setPortalAllowFileUpload] = useState<boolean>(!!portalConfig?.allowFileUpload);
	const [portalHideTimeEntries, setPortalHideTimeEntries] = useState<boolean>(!!portalConfig?.hideTimeEntries);
	const [portalHideEstimates, setPortalHideEstimates] = useState<boolean>(!!portalConfig?.hideEstimates);

	// Account manager: the internal person who owns this client relationship. Part
	// of core details (not the portal section), but persisted to the portal
	// alongside the rest of the client's portal config. Held as a Relay global id so
	// it matches the person list; the proxy decodes it to the raw person id.
	const accountManagerCandidates: Array<{id: string; name: string}> =
		viewer?.company?.allPersons?.edges?.flatMap(e => (e?.node ? [{id: e.node.id, name: e.node.fullName || ''}] : [])) ?? [];
	const initialAccountManagerId =
		accountManagerCandidates.find(p => decodePersonId(p.id) === portalConfig?.accountManagerForecastPersonId)?.id ?? null;
	const [accountManagerId, setAccountManagerId] = useState<string | null>(initialAccountManagerId);

	const selectedStatus = clientStatuses.find(s => s.id === statusId) ?? null;

	const onClickChangeAvatar = () => changeAvatarButtonRef.current?.click();

	const onChangeAvatar = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;
		Util.confirmFilePreviewType(file, res => {
			if (res) {
				setLogoState(LogoState.UPDATE);
				setSelectedFile(file);
				setLogo(URL.createObjectURL(file));
			} else {
				closeModal();
				showModal({
					type: MODAL_TYPE.GENERIC,
					content: (
						<div className="default-warning-modal">
							<Warning messageId="warning_modal.profile_picture_wrong_format_title" />
							<div className="warning-part-2">
								{formatMessage({id: 'warning_modal.profile_picture_wrong_format_info'})}
							</div>
						</div>
					),
					buttons: [
						{
							text: formatMessage({id: 'common.cancel'}),
							style: BUTTON_STYLE.FILLED,
							color: BUTTON_COLOR.WHITE,
						},
					],
				});
			}
		});
	};

	const onClickRemoveAvatar = () => {
		setLogoState(LogoState.DELETE);
		setSelectedFile(null);
		setLogo(getDefaultMediumClientLogo(client?.logoDefaultId));
	};

	const hasNewClientsFeature = hasFeatureFlag('new_clients_feature');

	const onClickSave = () => {
		const mutationBody = {
			id: clientId,
			name,
			...(hasNewClientsFeature
				? {website, region, statusId: statusId || undefined, primaryContactId: primaryContact?.id || null}
				: {}),
			street,
			zip,
			city,
			vat,
			country: country?.value || '',
			...(logoState === LogoState.DELETE ? {logoId: 'delete'} : {}),
		};

		const onSuccess = () => {
			showChangesSavedToast(formatMessage);
			closeModal();
		};

		// FOR-48: persist portal config (branding + flags) via the portal proxy
		// mutation, keyed by the Forecast client id (the graphql proxy decodes the
		// global id to the portal's psa_client_external_id). Fired only when something
		// actually changed, so editing a client's name/address doesn't write a portal
		// config row for a client that was never a portal client.
		const portalDirty =
			portalActive !== !!portalConfig?.portalActive ||
			(portalPrimaryColour || '') !== (portalConfig?.portalPrimaryColour || '') ||
			(portalSenderName || '') !== (portalConfig?.portalSenderName || '') ||
			portalShowInvoices !== !!portalConfig?.showInvoices ||
			portalAllowFileUpload !== !!portalConfig?.allowFileUpload ||
			portalHideTimeEntries !== !!portalConfig?.hideTimeEntries ||
			portalHideEstimates !== !!portalConfig?.hideEstimates ||
			accountManagerId !== initialAccountManagerId;
		if (showPortalSection && portalDirty) {
			Util.CommitMutation(UpdateClientPortalConfigMutation, {
				clientOrgId: String(clientId),
				// Lets the portal name the client org it provisions on first touch —
				// activating the workspace is usually the first portal write for a
				// client, before any sync has linked it.
				clientName: name,
				// null unassigns the owner; the field is always sent because the
				// dirty check above already established something changed.
				accountManagerId,
				portalActive,
				portalPrimaryColour: portalPrimaryColour || null,
				portalSenderName: portalSenderName || null,
				showInvoices: portalShowInvoices,
				allowFileUpload: portalAllowFileUpload,
				hideTimeEntries: portalHideTimeEntries,
				hideEstimates: portalHideEstimates,
			});
		}

		if (logoState === LogoState.UPDATE) {
			Util.CommitMutation(UpdateClientMutation, mutationBody, onSuccess, true, {
				id: clientId,
				file: selectedFile,
			});
		} else {
			Util.CommitMutation(UpdateClientMutation, mutationBody, onSuccess);
		}
	};

	const isRemoveLogoDisabled = (!client?.logoId && logoState !== LogoState.UPDATE) || logoState === LogoState.DELETE;
	const isUploadingNew = logoState === LogoState.DELETE || (!client?.logoId && logoState !== LogoState.UPDATE);

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id="clients.edit-client" defaultMessage="Edit Client" />
			</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'l'}>
					{/* Client Name */}
					<TextField
						label={formatMessage({id: 'clients.client-name'})}
						value={name}
						onValueChange={setName}
						size="large"
						width="flex"
					/>

					{/* Status */}
					{hasNewClientsFeature && (
						<FlexColumn gap={'s'}>
							<Label>
								<FormattedMessage id="common.status" />
							</Label>
							<DropdownList>
								<DropdownList.Trigger>
									<ButtonDropdownField>
										<Text>{selectedStatus?.label ?? ''}</Text>
									</ButtonDropdownField>
								</DropdownList.Trigger>
								<DropdownList.Content align={'start'}>
									{clientStatuses.map(status => (
										<DropdownList.Item key={status.id} onClick={() => setStatusId(status.id)}>
											<FlexRow>
												<Text>{status.label}</Text>
												{status.id === statusId && <Icon icon={'iconTick'} />}
											</FlexRow>
										</DropdownList.Item>
									))}
								</DropdownList.Content>
							</DropdownList>
						</FlexColumn>
					)}

					{/* Client Contact — new clients feature + contacts module */}
					{hasNewClientsFeature && hasModule(MODULE_TYPES.NEW_CLIENT_CONTACTS_FEATURE) && (
						<ClientPrimaryContactDropdown
							clientId={String(clientId)}
							value={primaryContact}
							onChange={setPrimaryContact}
						/>
					)}

					{/* Account manager — the internal owner of this client relationship.
					    Core details, not the portal section, but persisted to the portal. */}
					{showPortalSection && (
						<FlexColumn gap={'s'}>
							<Label>
								<FormattedMessage id="client_portal.account_manager" defaultMessage="Account manager" />
							</Label>
							<ComboBox
								defaultItems={accountManagerCandidates}
								value={accountManagerId ?? undefined}
								onChange={(key: string | null) => setAccountManagerId(key ?? null)}
								width={'flex'}
								placeholder={formatMessage({
									id: 'client_portal.account_manager_placeholder',
									defaultMessage: 'Unassigned',
								})}
								data-cy="account-manager-dropdown"
							>
								{(person: {id: string; name: string}) => (
									<ComboBox.Item key={person.id} textValue={person.name}>
										{person.name}
									</ComboBox.Item>
								)}
							</ComboBox>
						</FlexColumn>
					)}

					{/* Website */}
					{hasNewClientsFeature && (
						<TextField
							label={formatMessage({id: 'clients.website'})}
							value={website}
							onValueChange={setWebsite}
							size="large"
							width="flex"
						/>
					)}

					{/* View more / View less toggle */}
					<FlexRow gap={'s'} alignItems={'center'}>
						<div style={{flex: 1, height: 1, background: 'var(--border-decorative)'}} />
						<Button emphasis="low" onPress={() => setIsAddressExpanded(prev => !prev)}>
							{formatMessage({
								id: isAddressExpanded ? 'clients.view-less' : 'clients.view-more',
								defaultMessage: isAddressExpanded ? 'View Less' : 'View More',
							})}
						</Button>
						<div style={{flex: 1, height: 1, background: 'var(--border-decorative)'}} />
					</FlexRow>

					{/* Address section */}
					{isAddressExpanded && (
						<FlexColumn gap={'l'}>
							<TextField
								label={formatMessage({id: 'clients.street'})}
								value={street}
								onValueChange={setStreet}
								size="large"
								width="flex"
							/>
							<FlexRow gap={'l'}>
								<TextField
									label={formatMessage({id: 'clients.city'})}
									value={city}
									onValueChange={setCity}
									size="large"
									width="flex"
								/>
								<TextField
									label={formatMessage({id: 'clients.zip'})}
									value={zip}
									onValueChange={setZip}
									size="large"
									width="flex"
								/>
							</FlexRow>
							{hasNewClientsFeature && (
								<TextField
									label={formatMessage({id: 'clients.region'})}
									value={region}
									onValueChange={setRegion}
									size="large"
									width="flex"
								/>
							)}
							<FlexColumn gap={'s'}>
								<Label>
									<FormattedMessage id="clients.country" />
								</Label>
								<DropdownList>
									<DropdownList.Trigger>
										<ButtonDropdownField>
											<Text>{country?.label ?? ''}</Text>
										</ButtonDropdownField>
									</DropdownList.Trigger>
									<DropdownList.Content align={'start'}>
										{isoCountries.map((c: IsoCountryType) => (
											<DropdownList.Item key={c.value} onClick={() => setCountry(c)}>
												<FlexRow>
													<Text>{c.label}</Text>
													{c.value === country?.value && <Icon icon={'iconTick'} />}
												</FlexRow>
											</DropdownList.Item>
										))}
									</DropdownList.Content>
								</DropdownList>
							</FlexColumn>
							<FlexColumn gap={'s'}>
								<FlexRow gap={'xs'} alignItems={'center'}>
									<Label>
										<FormattedMessage id="clients.tax-code" />
									</Label>
									<IconTooltip>
										<IconTooltip.Item
											title={formatMessage({
												id: 'clients.tax-code-tooltip',
												defaultMessage: 'Tax / VAT code',
											})}
										/>
									</IconTooltip>
								</FlexRow>
								<TextField value={vat} onValueChange={setVat} size="large" width="flex" />
							</FlexColumn>
						</FlexColumn>
					)}

					{/* Logo */}
					<FlexRow gap={'l'} alignItems={'center'}>
						<FlexContainer>
							<img
								crossOrigin="use-credentials"
								data-private
								alt="client-logo"
								src={logo}
								width="96"
								height="96"
							/>
						</FlexContainer>
						<FlexColumn>
							<input
								type="file"
								style={{display: 'none'}}
								onChange={onChangeAvatar}
								ref={changeAvatarButtonRef}
								accept=".jpg,.jpeg,.png,.gif"
							/>
							<Button emphasis="low" onPress={onClickChangeAvatar}>
								{formatMessage({id: isUploadingNew ? 'clients.upload-logo' : 'clients.update-logo'})}
							</Button>
							{!isRemoveLogoDisabled && (
								<Button emphasis="low" onPress={onClickRemoveAvatar}>
									{formatMessage({id: 'clients.remove-logo', defaultMessage: 'Remove Logo'})}
								</Button>
							)}
						</FlexColumn>
					</FlexRow>

					{/* FOR-48 (BU-10) 10a — Client Portal section. Availability-gated;
					    read-only with an "Activate portal" prompt when the portal is off.
					    Branding lives here only (NOT in global Settings). */}
					{showPortalSection && (
						<FlexColumn gap={'l'} data-cy="client-portal-section">
							<FlexRow gap={'s'} alignItems={'center'}>
								<div style={{flex: 1, height: 1, background: 'var(--border-decorative)'}} />
								<Label>
									<FormattedMessage id="client_portal.section_title" defaultMessage="Client Workspace" />
								</Label>
								<div style={{flex: 1, height: 1, background: 'var(--border-decorative)'}} />
							</FlexRow>

							<Checkbox
								checked={portalActive}
								onChange={() => setPortalActive(prev => !prev)}
								data-cy="portal-active-toggle"
								labelText={formatMessage({
									id: 'client_portal.active_toggle',
									defaultMessage: 'Workspace active',
								})}
							/>

							{!portalActive && (
								<Text color={'secondary'}>
									<FormattedMessage
										id="client_portal.activate_prompt"
										defaultMessage="Activate the portal to configure branding and features."
									/>
								</Text>
							)}

							{portalActive && (
								<FlexColumn gap={'l'}>
									<TextField
										label={formatMessage({
											id: 'client_portal.primary_colour',
											defaultMessage: 'Primary colour (hex)',
										})}
										value={portalPrimaryColour}
										onValueChange={setPortalPrimaryColour}
										size="large"
										width="flex"
									/>
									<TextField
										label={formatMessage({
											id: 'client_portal.sender_name',
											defaultMessage: 'Email sender name',
										})}
										value={portalSenderName}
										onValueChange={setPortalSenderName}
										size="large"
										width="flex"
									/>
									<Checkbox
										checked={portalShowInvoices}
										onChange={() => setPortalShowInvoices(p => !p)}
										labelText={formatMessage({
											id: 'client_portal.flag_show_invoices',
											defaultMessage: 'Show invoices',
										})}
									/>
									<Checkbox
										checked={portalAllowFileUpload}
										onChange={() => setPortalAllowFileUpload(p => !p)}
										labelText={formatMessage({
											id: 'client_portal.flag_allow_file_upload',
											defaultMessage: 'Allow file upload',
										})}
									/>
									<Checkbox
										checked={portalHideTimeEntries}
										onChange={() => setPortalHideTimeEntries(p => !p)}
										labelText={formatMessage({
											id: 'client_portal.flag_hide_time_entries',
											defaultMessage: 'Hide time entries',
										})}
									/>
									<Checkbox
										checked={portalHideEstimates}
										onChange={() => setPortalHideEstimates(p => !p)}
										labelText={formatMessage({
											id: 'client_portal.flag_hide_estimates',
											defaultMessage: 'Hide estimates',
										})}
									/>
								</FlexColumn>
							)}
						</FlexColumn>
					)}

					{/* FOR-82 (BU-10e) — Workspace users section (provision/invite/edit/deactivate).
					    Rendered only when the workspace is active. */}
					{showPortalSection && client && <WorkspaceUsersSection client={client} workspaceActive={portalActive} />}
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={onClickSave}>
					{formatMessage({id: 'clients.save-client', defaultMessage: 'Save Client'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal}>
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export const EditClientDetailsModalQuery = graphql`
	query EditClientDetailsModal_Query($clientId: String) {
		viewer {
			actualPersonId
			component(name: "edit_client_details_modal")
			...EditClientDetailsModal_viewer @arguments(clientId: $clientId)
		}
	}
`;

export default createFragmentContainer(EditClientDetailsModal, {
	viewer: graphql`
		fragment EditClientDetailsModal_viewer on Viewer @argumentDefinitions(clientId: {type: "String"}) {
			id
			availableFeatureFlags {
				key
			}
			company {
				id
				tier
				clientStatuses(first: 100) {
					edges {
						node {
							id
							label
							standing
						}
					}
				}
				# Candidate account managers: active internal people (client users
				# excluded — an account manager is firm-side).
				allPersons(first: 10000, onlyActive: true, excludeClientUsers: true) {
					edges {
						node {
							id
							fullName
						}
					}
				}
				client(id: $clientId) {
					id
					name
					street
					zip
					city
					vat
					country
					region
					website
					notes
					logoId
					logoDefaultId
					status {
						id
						label
						standing
					}
					primaryContact {
						id
						firstName
						lastName
					}
					portalConfig {
						portalActive
						portalLogoUrl
						portalPrimaryColour
						portalSenderName
						accountManagerForecastPersonId
						showInvoices
						allowFileUpload
						hideTimeEntries
						hideEstimates
					}
					portalUsers {
						id
						email
						roleKey
						status
						scopeLevel
						scopeProgrammeIds
						scopeProjectIds
					}
					clientPortalHierarchy {
						programmes {
							id
							name
						}
						projects {
							id
							name
							programmeId
						}
					}
				}
			}
		}
	`,
});
